import { BrandId, FragranceId, Gender, Longevity, NoteId, Occasion } from '~/api/api.declaration';
import { ApiService } from '~/service/service.api';
import { FragranceImportDto, FragrancePhotoImportDto } from '~/shared/dto/admin/fragrance.dto';
import { OlfactiveFamilyId, PerfumerId } from '~/shared/dto/id';
import { FragrancePhotoStatus, Season, Sillage, Weather } from '~/shared/dto/model.enums';
import { Model } from '~/shared/service/service.base';
import { ImportModel } from './model.import';

type FragranceRowType = Omit<InstanceType<typeof FragranceImportDto>, 'id'> & {
	fragranceId: FragranceId;
	authors: PerfumerId[];
	images: string[];
};

@Model()
export class FragranceImportModel extends ImportModel<FragranceImportDto> {
	constructor(api: ApiService) {
		super(FragranceImportDto, api.fragranceImport.bind(api));
	}

	hanldeClickDownloadTemplate = () => {
		const example: FragranceRowType = {
			fragranceId: 43,
			brandId: 42,
			name: 'Example Fragrance',
			active: true,
			images: ['fileId1', 'fileId2'],
			description: 'A fragrance description',
			discontinued: false,
			gender: Gender.MALE,
			years: [2010, 2015],
			authors: [46, 34] as PerfumerId[],
			olfactives: [188, 12, 45] as OlfactiveFamilyId[],
			synonyms: ['Sample', 'AltName'],
			brandBaseNotes: [44, 5] as NoteId[],
			brandMidNotes: [6, 44] as NoteId[],
			brandTopNotes: [8, 44] as NoteId[],
			noseNotes: [44, 11] as NoteId[],
			noseLongevity: Longevity.ABOUT_1_HOUR,
			noseSillage: Sillage.CLOSE_TO_SKIN,
			noseOccasion: [Occasion.DAILY, Occasion.LEASURE],
			noseSeason: [Season.SUMMER, Season.SPRING],
			noseWeather: [Weather.SUNNY, Weather.SNOW],
		};

		this.buildAndDownloadCSV('fragrance-import-template.csv', example);
	};

	getItemFromRow(row: FragranceRowType): FragranceImportDto {
		return {
			id: this.parseNumber(row.fragranceId) as FragranceId,
			brandId: this.parseNumber(row.brandId) as BrandId,
			name: row.name,
			active: this.parseBoolean(row.active),
			description: row.description,
			publicPhotos: this.parseStringArray(row.images)?.map(fileId => ({ fileId, url: '' })),
			discontinued: this.parseBoolean(row.discontinued),
			gender: this.parseEnum(Gender, row.gender),
			years: this.parseNumberArray(row.years),
			perfumers: this.parseNumberArray<PerfumerId>(row.authors),
			olfactives: this.parseNumberArray<OlfactiveFamilyId>(row.olfactives),
			brandBaseNotes: this.parseNumberArray(row.brandBaseNotes),
			brandMidNotes: this.parseNumberArray(row.brandMidNotes),
			brandTopNotes: this.parseNumberArray(row.brandTopNotes),
			noseNotes: this.parseNumberArray(row.noseNotes),
			noseLongevity: this.parseEnum(Longevity, row.noseLongevity),
			noseSillage: this.parseEnum(Sillage, row.noseSillage),
			noseOccasion: this.parseEnumArray(Occasion, row.noseOccasion),
			noseSeason: this.parseEnumArray(Season, row.noseSeason),
			noseWeather: this.parseEnumArray(Weather, row.noseWeather),
			synonyms: this.parseStringArray(row.synonyms),
		};
	}
}

type FragrancePhotoType = InstanceType<typeof FragrancePhotoImportDto>;

@Model()
export class FragrancePhotoImportModel extends ImportModel<FragrancePhotoImportDto> {
	constructor(api: ApiService) {
		super(FragrancePhotoImportDto, api.fragranceImportPhotos.bind(api));
	}

	getItemFromRow(row: FragrancePhotoType): FragrancePhotoImportDto {
		return {
			fileId: row.fileId,
			fragranceId: this.parseNumber(row.fragranceId) as FragranceId,
			status: this.parseEnum(FragrancePhotoStatus, row.status) ?? FragrancePhotoStatus.PUBLIC,
		};
	}

	hanldeClickDownloadTemplate = () => {
		const example: FragrancePhotoType = {
			fileId: 'fileId1',
			fragranceId: 43,
			status: FragrancePhotoStatus.PUBLIC,
		};

		this.buildAndDownloadCSV('fragrance-photo-import-template.csv', example);
	};
}
